import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Typography, IconButton, Alert, CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, Send as SendIcon } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

const C = {
  bg: '#111827',
  cardBg: '#1a2236',
  border: '#1e293b',
  accent: '#2563eb',
  gradient: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
  text1: '#f1f5f9',
  text2: '#cbd5e1',
  text3: '#64748b',
};

export default function ContactDialog({ open, onClose, defaultSubject = '' }) {
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', subject: defaultSubject, message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in your name, email, and message.');
      return;
    }
    setSending(true);
    setError('');
    try {
      await axios.post(`${API_URL}/contact`, form);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send. Please email info@cargo-command.com directly.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset after animation
    setTimeout(() => {
      setForm({ name: '', email: '', company: '', phone: '', subject: defaultSubject, message: '' });
      setSent(false);
      setError('');
    }, 300);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: C.bg, border: `1px solid ${C.border}`, borderRadius: '16px', backgroundImage: 'none' } }}>

      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', color: C.text1 }}>
          {sent ? 'Message Sent!' : 'Get in Touch'}
        </Typography>
        <IconButton onClick={handleClose} size="small" sx={{ color: C.text3 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {sent ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography sx={{ fontSize: '3rem', mb: 2 }}>✅</Typography>
            <Typography sx={{ color: C.text1, fontWeight: 600, fontSize: '1.1rem', mb: 1 }}>
              Thanks, {form.name.split(' ')[0]}!
            </Typography>
            <Typography sx={{ color: C.text3, fontSize: '0.92rem' }}>
              We'll get back to you within 1 business day at {form.email}.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography sx={{ color: C.text3, fontSize: '0.88rem', mb: 1 }}>
              Fill out the form and we'll reach out within 1 business day.
            </Typography>

            {error && <Alert severity="error" sx={{ bgcolor: 'rgba(239,68,68,0.1)', color: '#f87171' }}>{error}</Alert>}

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField name="name" label="Name *" value={form.name} onChange={handleChange} fullWidth size="small"
                sx={fieldSx} InputLabelProps={{ sx: { color: C.text3 } }} />
              <TextField name="email" label="Email *" type="email" value={form.email} onChange={handleChange} fullWidth size="small"
                sx={fieldSx} InputLabelProps={{ sx: { color: C.text3 } }} />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField name="company" label="Company" value={form.company} onChange={handleChange} fullWidth size="small"
                sx={fieldSx} InputLabelProps={{ sx: { color: C.text3 } }} />
              <TextField name="phone" label="Phone" value={form.phone} onChange={handleChange} fullWidth size="small"
                sx={fieldSx} InputLabelProps={{ sx: { color: C.text3 } }} />
            </Box>

            <TextField name="subject" label="Subject" value={form.subject} onChange={handleChange} fullWidth size="small"
              sx={fieldSx} InputLabelProps={{ sx: { color: C.text3 } }} />

            <TextField name="message" label="Message *" value={form.message} onChange={handleChange}
              fullWidth multiline rows={4} size="small"
              placeholder="Tell us about your fleet and what you're looking for..."
              sx={fieldSx} InputLabelProps={{ sx: { color: C.text3 } }} />
          </Box>
        )}
      </DialogContent>

      {!sent && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} sx={{ color: C.text3, textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={sending}
            endIcon={sending ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SendIcon />}
            sx={{
              background: C.gradient, textTransform: 'none', fontWeight: 700,
              borderRadius: '10px', px: 4, py: 1,
              '&:hover': { boxShadow: '0 6px 24px rgba(37,99,235,0.4)' },
              '&.Mui-disabled': { background: C.gradient, opacity: 0.6 },
            }}>
            {sending ? 'Sending...' : 'Send Message'}
          </Button>
        </DialogActions>
      )}

      {sent && (
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
          <Button variant="contained" onClick={handleClose}
            sx={{ background: C.gradient, textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 5, py: 1 }}>
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#f1f5f9',
    '& fieldset': { borderColor: '#1e293b' },
    '&:hover fieldset': { borderColor: '#2563eb' },
    '&.Mui-focused fieldset': { borderColor: '#2563eb' },
  },
  '& .MuiInputBase-input::placeholder': { color: '#64748b' },
};
